import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, MessageCircle, ArrowRight } from 'lucide-react';
import { fetchCollections } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Footer } from '@/components/Footer';
import { useApp } from '@/state/AppContext';

const CollectionsPage = () => {
  const { config } = useApp();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollections().then(setCollections).finally(() => setLoading(false));
  }, []);

  const wa = `https://wa.me/${config.whatsapp_number}?text=${encodeURIComponent('Hi Big Bash, please tell me when your festival collections launch!')}`;

  return (
    <div className="bg-white" data-testid="collections-page">
      <section className="bb-block-cyan">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--bb-yellow)] text-[var(--bb-darkblue)] text-xs font-nunito-black uppercase tracking-wider">
            <Bell className="w-4 h-4" /> Launching Soon
          </div>
          <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[0.95] text-white">
            Festival Collections
          </h1>
          <p className="text-sm md:text-base text-white/95 mt-3 font-nunito-medium max-w-xl">
            Curated chocolate bundles for Diwali, Mother's Day, Raksha Bandhan and more. Drop us your number on WhatsApp and we'll ping you the day they launch.
          </p>
          <Button asChild className="mt-5 h-12 px-5 rounded-xl bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] text-white font-nunito-black">
            <a href={wa} target="_blank" rel="noreferrer"><MessageCircle className="w-4 h-4 mr-1.5" /> Notify me on WhatsApp</a>
          </Button>
        </div>
      </section>

      {/* Banner-style collection layout (wide horizontal banners) */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 mt-6 space-y-4">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-2xl border-2 border-[var(--bb-darkblue)]/10 h-44 md:h-56 bg-[var(--bb-yellow-soft)] animate-pulse" />
            ))
          : collections.map((c, idx) => {
              // Rotate accent colors
              const accents = [
                { bg: 'bb-block-cyan', text: 'text-white', tagBg: 'bg-[var(--bb-yellow)]', tagText: 'text-[var(--bb-darkblue)]' },
                { bg: 'bb-block-pink', text: 'text-white', tagBg: 'bg-white', tagText: 'text-[var(--bb-pink)]' },
                { bg: 'bb-block-yellow', text: 'text-[var(--bb-darkblue)]', tagBg: 'bg-[var(--bb-darkblue)]', tagText: 'text-white' },
                { bg: 'bb-block-dark', text: 'text-white', tagBg: 'bg-[var(--bb-yellow)]', tagText: 'text-[var(--bb-darkblue)]' },
              ];
              const a = accents[idx % accents.length];
              return (
                <Link
                  key={c.slug}
                  to={`/collections/${c.slug}`}
                  className={`group relative rounded-3xl overflow-hidden ${a.bg} ${a.text} grid md:grid-cols-[1fr_280px] lg:grid-cols-[1fr_360px] gap-0 hover:-translate-y-1 transition-transform`}
                  data-testid={`collection-card-${c.slug}`}
                >
                  <div className="p-6 md:p-8 flex flex-col justify-center gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={`rounded-full text-[10px] font-nunito-black ${a.tagBg} ${a.tagText}`}>
                        {c.audience === 'corporate' ? 'Corporate gifting' : c.coming_soon ? 'Festival' : 'For Everyone'}
                      </Badge>
                      {c.coming_soon && (
                        <Badge className="rounded-full text-[10px] font-nunito-black bg-white text-[var(--bb-darkblue)]">
                          Coming Soon
                        </Badge>
                      )}
                    </div>
                    <div className="font-display text-3xl md:text-5xl leading-[0.95]">{c.name}</div>
                    <div className="text-xs md:text-sm font-nunito-bold opacity-90">{c.tagline}</div>
                    <div className="text-sm md:text-base font-nunito-medium opacity-90 max-w-xl line-clamp-3">{c.description}</div>
                    <div className="mt-2 inline-flex items-center gap-1 text-sm font-nunito-black">
                      {c.coming_soon ? 'Get notified' : 'Explore'}
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </div>
                  </div>
                  <div className="relative h-48 md:h-auto bg-white/15 overflow-hidden">
                    <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                    {c.coming_soon && (
                      <div className="absolute inset-0 bg-[var(--bb-darkblue)]/30 backdrop-blur-[1px] flex items-center justify-center">
                        <span className="px-3 py-1.5 rounded-full bg-[var(--bb-yellow)] text-[var(--bb-darkblue)] text-xs font-nunito-black uppercase tracking-wider">
                          Coming Soon
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
      </section>
      <Footer />
    </div>
  );
};

export default CollectionsPage;
